import {
  type ISeriesPrimitive,
  type IPrimitivePaneView,
  type IPrimitivePaneRenderer,
  type SeriesAttachedParameter,
  type IChartApiBase,
  type ISeriesApi,
  type SeriesType,
  type Time,
} from "lightweight-charts"

const LARGE_TRADE_THRESHOLD_TCO2E = 10

interface CanvasTarget {
  useBitmapCoordinateSpace<T>(
    f: (scope: {
      context: CanvasRenderingContext2D
      horizontalPixelRatio: number
      verticalPixelRatio: number
      bitmapSize: { width: number; height: number }
    }) => T,
  ): T
}

export interface FillBucket {
  time: number
  price: number
  volume: number
  side: number // 1 = bid aggressor, 2 = ask aggressor
  aggressorSide: number | null // 1=buyer was taker (green), 2=seller was taker (red); null=legacy
}

export interface BubbleParams {
  buckets: FillBucket[]
}

class BubbleRenderer implements IPrimitivePaneRenderer {
  constructor(
    private _chart: IChartApiBase<Time>,
    private _series: ISeriesApi<SeriesType, Time>,
    private _p: BubbleParams,
  ) {}

  draw(target: CanvasTarget): void {
    target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const { buckets } = this._p
      if (buckets.length === 0) return
      const timeScale = this._chart.timeScale()

      // Price line connecting fill buckets
      if (buckets.length >= 2) {
        ctx.beginPath()
        ctx.strokeStyle = "rgba(220,230,255,0.9)"
        ctx.lineWidth = Math.round(1.5 * hpr)
        let moved = false
        for (const b of buckets) {
          const x = timeScale.timeToCoordinate(Math.floor(b.time / 1000) as Time)
          const y = this._series.priceToCoordinate(b.price)
          if (x === null || y === null) continue
          const xPx = Math.round(x * hpr)
          const yPx = Math.round(y * vpr)
          if (!moved) {
            ctx.moveTo(xPx, yPx)
            moved = true
          } else {
            ctx.lineTo(xPx, yPx)
          }
        }
        ctx.stroke()
      }

      // Pass 1 — Small fills (volume < threshold): hollow circles, keep chart clean
      for (const bucket of buckets) {
        if (bucket.volume >= LARGE_TRADE_THRESHOLD_TCO2E) continue
        const x = timeScale.timeToCoordinate(Math.floor(bucket.time / 1000) as Time)
        const y = this._series.priceToCoordinate(bucket.price)
        if (x === null || y === null) continue
        const xPx = Math.round(x * hpr)
        const yPx = Math.round(y * vpr)
        // aggressorSide: 1 = buyer was taker (crossed spread) = green, 2 = seller was taker = red
        // Fall back to side-based heuristic for legacy rows without aggressorSide
        const isBuyFill = bucket.aggressorSide != null
          ? bucket.aggressorSide === 1
          : bucket.side === 2
        const [r, g, b] = isBuyFill ? [0, 220, 100] : [240, 60, 60]
        const radiusPx = Math.max(5 * hpr, Math.min(Math.sqrt(bucket.volume) * 1.3 * hpr, 32 * hpr))
        ctx.beginPath()
        ctx.arc(xPx, yPx, radiusPx, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(${r},${g},${b},0.16)`
        ctx.fill()
        ctx.strokeStyle = `rgba(${r},${g},${b},0.8)`
        ctx.lineWidth = 1.5 * hpr
        ctx.stroke()
      }

      // Pass 2 — Large fills (volume >= threshold): outer halo + solid inner fill
      for (const bucket of buckets) {
        if (bucket.volume < LARGE_TRADE_THRESHOLD_TCO2E) continue
        const x = timeScale.timeToCoordinate(Math.floor(bucket.time / 1000) as Time)
        const y = this._series.priceToCoordinate(bucket.price)
        if (x === null || y === null) continue
        const xPx = Math.round(x * hpr)
        const yPx = Math.round(y * vpr)
        const isBuyFill = bucket.aggressorSide != null
          ? bucket.aggressorSide === 1
          : bucket.side === 2
        const [r, g, b] = isBuyFill ? [0, 220, 100] : [240, 60, 60]
        const radiusPx = Math.max(5 * hpr, Math.min(Math.sqrt(bucket.volume) * 1.3 * hpr, 32 * hpr))
        const haloR = radiusPx * 1.6

        // Outer halo
        const halo = ctx.createRadialGradient(xPx, yPx, 0, xPx, yPx, haloR)
        halo.addColorStop(0, `rgba(${r},${g},${b},0.25)`)
        halo.addColorStop(1, `rgba(${r},${g},${b},0)`)
        ctx.beginPath()
        ctx.arc(xPx, yPx, haloR, 0, Math.PI * 2)
        ctx.fillStyle = halo
        ctx.fill()

        // Inner fill: radial gradient from center to edge
        const fill = ctx.createRadialGradient(xPx, yPx, 0, xPx, yPx, radiusPx)
        fill.addColorStop(0, `rgba(${r},${g},${b},0.9)`)
        fill.addColorStop(1, `rgba(${r},${g},${b},0.4)`)
        ctx.beginPath()
        ctx.arc(xPx, yPx, radiusPx, 0, Math.PI * 2)
        ctx.fillStyle = fill
        ctx.fill()

        // Border
        ctx.strokeStyle = `rgba(${r},${g},${b},1.0)`
        ctx.lineWidth = 2 * hpr
        ctx.stroke()

        // Volume label — only when bubble is large enough to be readable
        const visualRadius = radiusPx / hpr
        if (visualRadius >= 9) {
          const label =
            bucket.volume >= 1000
              ? `${Math.round(bucket.volume / 100) / 10}k`
              : `${Math.round(bucket.volume)}`
          const fontSizeCss = Math.max(8, Math.round(visualRadius * 0.85))
          ctx.font = `bold ${fontSizeCss}px sans-serif`
          ctx.textAlign = "center"
          ctx.textBaseline = "middle"
          ctx.fillStyle = "rgba(255,255,255,0.95)"
          ctx.fillText(label, xPx / hpr, yPx / vpr)
        }
      }
    })
  }
}

class BubblePaneView implements IPrimitivePaneView {
  constructor(
    private _chart: IChartApiBase<Time>,
    private _series: ISeriesApi<SeriesType, Time>,
    private _p: BubbleParams,
  ) {}

  renderer(): IPrimitivePaneRenderer {
    return new BubbleRenderer(this._chart, this._series, this._p)
  }
}

export class BubblePrimitive implements ISeriesPrimitive<Time> {
  private _p: BubbleParams
  private _chart: IChartApiBase<Time> | null = null
  private _series: ISeriesApi<SeriesType, Time> | null = null
  private _requestUpdate: (() => void) | null = null

  constructor(p: BubbleParams) {
    this._p = p
  }

  attached({ chart, series, requestUpdate }: SeriesAttachedParameter<Time>): void {
    this._chart = chart
    this._series = series
    this._requestUpdate = requestUpdate
  }

  detached(): void {
    this._chart = null
    this._series = null
    this._requestUpdate = null
  }

  updateParams(params: Partial<BubbleParams>): void {
    this._p = { ...this._p, ...params }
    this._requestUpdate?.()
  }

  paneViews(): readonly IPrimitivePaneView[] {
    if (!this._chart || !this._series) return []
    return [new BubblePaneView(this._chart, this._series, this._p)]
  }
}
